import { useCallback } from 'react';
import {
  useCreateBenchmarkRoute,
  useCreateFullRouteList,
  useGeneratePartialCommercialFlights,
  useGeneratePartialRoute,
  useStartCommercialFlightsList,
  useStartSurgeonRouteList,
} from '@/queries/mission-planner';
import { useSendSuggestedRoutesMetrics } from '@/queries/route-planner';
import { GenerateBenchmarkBody, GenerateMultiOrPlanBody, GeneratePartialPlanBody } from '@/api/apis/mission-planner';
import { SuggestedRouteMetricsType } from '@/api/apis/route';

export const useSuggestedMutations = () => {
  const { mutateAsync: createBenchmark } = useCreateBenchmarkRoute();
  const { mutateAsync: createFullRouteList } = useCreateFullRouteList();
  const { mutateAsync: startSurgeonRouteList } = useStartSurgeonRouteList();
  const { mutateAsync: startCommercialFlightsList } = useStartCommercialFlightsList();
  const { mutateAsync: generatePartialRoute } = useGeneratePartialRoute();
  const { mutateAsync: generatePartialCommercialFlights } = useGeneratePartialCommercialFlights();
  const { mutateAsync: sendSuggestedRouteMetrics } = useSendSuggestedRoutesMetrics();

  const createBenchmarkRouteTask = useCallback(
    async (data: GenerateBenchmarkBody) => {
      const { taskId } = await createBenchmark(data);

      return taskId;
    },
    [createBenchmark],
  );

  const createSuggestedRoutesTask = useCallback(
    async (data: GenerateMultiOrPlanBody) => {
      const { taskId } = await createFullRouteList(data);

      return taskId;
    },
    [createFullRouteList],
  );

  const createSurgeonTrips = useCallback(
    async (planId: string, forcedSurgeonId: string) => {
      await startSurgeonRouteList({ taskId: planId, forcedSurgeonId });
    },
    [startSurgeonRouteList],
  );

  const createCommercialFlightsTrips = useCallback(
    async (planId: string) => {
      await startCommercialFlightsList(planId);
    },
    [startCommercialFlightsList],
  );

  const createPartialRoutesTask = useCallback(
    async (data: GeneratePartialPlanBody) => {
      const { taskId } = await generatePartialRoute(data);

      return taskId;
    },
    [generatePartialRoute],
  );

  const createPartialCommercialFlightsTrips = useCallback(
    async (planId: string) => {
      await generatePartialCommercialFlights(planId);
    },
    [generatePartialCommercialFlights],
  );

  const setGeneratedRoutesStatistics = useCallback(
    async (data: SuggestedRouteMetricsType) => {
      await sendSuggestedRouteMetrics(data).catch(() => {});
    },
    [sendSuggestedRouteMetrics],
  );

  return {
    createBenchmarkRouteTask,
    createSuggestedRoutesTask,
    createSurgeonTrips,
    createCommercialFlightsTrips,
    createPartialRoutesTask,
    createPartialCommercialFlightsTrips,
    setGeneratedRoutesStatistics,
  };
};
